import {
  ORDER_STATUSES,
  ORDER_STATUS_LABELS,
  LEAD_STATUSES,
  LEAD_STATUS_LABELS,
  SERVICE_BOOKING_STATUSES,
  SERVICE_BOOKING_STATUS_LABELS,
  SERVICE_KINDS,
  SERVICE_KIND_LABELS,
  TRIP_DATE_LIFECYCLES,
  INVOICE_STATUSES,
  INVOICE_STATUS_LABELS,
  INVOICE_TYPES,
  INVOICE_TYPE_LABELS,
  COMPLAINT_STATUSES,
  COMPLAINT_STATUS_LABELS,
  COMPLAINT_TYPES,
  COMPLAINT_TYPE_LABELS,
} from "./types";
import type { FieldSpec, JourneySubjectType } from "./types";

function opts<T extends string>(
  values: readonly T[],
  labels: Record<T, string>,
): Array<{ value: string; label: string }> {
  return values.map((v) => ({ value: v, label: labels[v] }));
}

function plain<T extends string>(values: readonly T[]): Array<{ value: string; label: string }> {
  return values.map((v) => ({ value: v, label: v }));
}

const RFM_LABELS = ["champion", "loyal", "potential", "new", "at_risk", "dormant", "lost"] as const;

/**
 * The single source of truth for which fields each subject type exposes to the journey builder —
 * powers BOTH the trigger picker (status enums, date fields for `time_offset`, numeric fields for
 * `field_change`) AND the entry/step condition builder. `key` MUST match the vars key produced by
 * `resolveSubject` so a condition/threshold actually resolves at runtime.
 */
export const FIELD_REGISTRY: Record<JourneySubjectType, FieldSpec[]> = {
  order: [
    {
      key: "status",
      label: "Status",
      type: "enum",
      status: true,
      options: opts(ORDER_STATUSES, ORDER_STATUS_LABELS),
    },
    { key: "totalPrice", label: "Total price", type: "number" },
    { key: "currency", label: "Currency", type: "string" },
    { key: "depositAmount", label: "Deposit amount", type: "number" },
    { key: "depositDueDate", label: "Deposit due date", type: "date", date: true },
    { key: "fullPaymentDue", label: "Full payment due", type: "date", date: true },
    { key: "tripStartDate", label: "Trip start date", type: "date", date: true },
    { key: "tripEndDate", label: "Trip end date", type: "date", date: true },
    { key: "adults", label: "Adults", type: "number" },
    { key: "children", label: "Children", type: "number" },
    { key: "source", label: "Source", type: "string" },
    { key: "createdAt", label: "Created at", type: "date", date: true },
  ],
  lead: [
    {
      key: "status",
      label: "Status",
      type: "enum",
      status: true,
      options: opts(LEAD_STATUSES, LEAD_STATUS_LABELS),
    },
    { key: "estimatedValue", label: "Estimated value", type: "number" },
    { key: "leadScore", label: "Lead score", type: "number" },
    { key: "source", label: "Source", type: "string" },
    { key: "createdAt", label: "Created at", type: "date", date: true },
    { key: "updatedAt", label: "Last activity", type: "date", date: true },
  ],
  customer: [
    { key: "rfmLabel", label: "RFM label", type: "enum", options: plain(RFM_LABELS) },
    { key: "totalSpent", label: "Total spent", type: "number" },
    { key: "tripsCount", label: "Trips count", type: "number" },
    { key: "score", label: "Score", type: "number" },
    { key: "country", label: "Country", type: "string" },
    { key: "lastTripDate", label: "Last trip date", type: "date", date: true },
    { key: "createdAt", label: "Created at", type: "date", date: true },
  ],
  service_booking: [
    {
      key: "status",
      label: "Status",
      type: "enum",
      status: true,
      options: opts(SERVICE_BOOKING_STATUSES, SERVICE_BOOKING_STATUS_LABELS),
    },
    { key: "kind", label: "Kind", type: "enum", options: opts(SERVICE_KINDS, SERVICE_KIND_LABELS) },
    { key: "cost", label: "Cost", type: "number" },
    { key: "confirmDeadline", label: "Confirm deadline", type: "date", date: true },
    { key: "paymentDeadline", label: "Payment deadline", type: "date", date: true },
    { key: "createdAt", label: "Created at", type: "date", date: true },
  ],
  trip_date: [
    {
      key: "lifecycle",
      label: "Lifecycle",
      type: "enum",
      status: true,
      options: plain(TRIP_DATE_LIFECYCLES),
    },
    { key: "startDate", label: "Start date", type: "date", date: true },
    { key: "endDate", label: "End date", type: "date", date: true },
    { key: "capacity", label: "Capacity", type: "number" },
    { key: "bookedCount", label: "Booked count", type: "number" },
  ],
  invoice: [
    {
      key: "status",
      label: "Status",
      type: "enum",
      status: true,
      options: opts(INVOICE_STATUSES, INVOICE_STATUS_LABELS),
    },
    { key: "type", label: "Type", type: "enum", options: opts(INVOICE_TYPES, INVOICE_TYPE_LABELS) },
    { key: "total", label: "Total", type: "number" },
    { key: "dueDate", label: "Due date", type: "date", date: true },
    { key: "issueDate", label: "Issue date", type: "date", date: true },
  ],
  review: [
    { key: "rating", label: "Rating", type: "number" },
    { key: "isApproved", label: "Approved", type: "boolean" },
    { key: "createdAt", label: "Created at", type: "date", date: true },
  ],
  complaint: [
    {
      key: "status",
      label: "Status",
      type: "enum",
      status: true,
      options: opts(COMPLAINT_STATUSES, COMPLAINT_STATUS_LABELS),
    },
    {
      key: "type",
      label: "Type",
      type: "enum",
      options: opts(COMPLAINT_TYPES, COMPLAINT_TYPE_LABELS),
    },
    { key: "dueDate", label: "Due date", type: "date", date: true },
    { key: "createdAt", label: "Created at", type: "date", date: true },
  ],
  participant: [
    { key: "passportExpiry", label: "Passport expiry", type: "date", date: true },
    { key: "nationality", label: "Nationality", type: "string" },
    { key: "createdAt", label: "Created at", type: "date", date: true },
  ],
};
